import React, { useEffect, useState, useCallback } from 'react';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import api from '../../../services/api';
import { getGoogleMapsLoaderOptions } from '../../../config/googleMaps';

interface ChassisData {
  asset_id: string;
  identifier: string;
  asset_class: string | null;
  chassis_type: string | null;
  door_type: string | null;
  latitude: number | null;
  longitude: number | null;
  altitude: number | null;
  velocity: number | null;
  is_moving: boolean | null;
  battery_state: boolean | null;
  has_contents: boolean | null;
  contents_percentage: number | null;
  door_open: boolean | null;
  temperature: number | null;
  humidity: number | null;
  last_reported_at: string | null;
  last_synced_at: string | null;
  equip_category: string | null;
  equip_make: string | null;
  equip_model: string | null;
  active_pronumber: number | null;
  active_container: string | null;
  matched_truck_identifier: string | null;
  matched_tag_serial: string | null;
  match_confidence: 'confirmed' | 'probable' | 'stale' | null;
  match_score: number | null;
  matched_since: string | null;
}

interface ChassisMapPanelProps {
  chassisIdentifier: string;
}

const REFRESH_INTERVAL_MS = 60_000;
const MAP_HEIGHT = 360;
const mapContainerStyle = { width: '100%', height: `${MAP_HEIGHT}px` };
const fallbackCenter = { lat: 29.7604, lng: -95.3698 };

function timeAgo(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export function matchChipText(c: {
  matched_truck_identifier: string | null;
  match_confidence: string | null;
  matched_since: string | null;
}): string {
  if (!c.matched_truck_identifier) return '—';
  const parts: string[] = [];
  if (c.match_confidence) parts.push(c.match_confidence);
  if (c.matched_since) parts.push(timeAgo(c.matched_since));
  return parts.length
    ? `${c.matched_truck_identifier} (${parts.join(', ')})`
    : c.matched_truck_identifier;
}

export const CONFIDENCE_CLASS: Record<string, string> = {
  confirmed: 'text-green-600 dark:text-green-400',
  probable: 'text-amber-600 dark:text-amber-400',
  stale: 'text-gray-400 dark:text-gray-500',
};

function InfoRow({
  label,
  value,
  valueClass = '',
  tooltip,
}: {
  label: string;
  value: string;
  valueClass?: string;
  tooltip?: string;
}) {
  return (
    <div className="flex justify-between items-center text-xs">
      <span className="text-muted">{label}</span>
      <span className={`text-gray-900 dark:text-gray-100 ${valueClass}`} title={tooltip}>
        {value}
      </span>
    </div>
  );
}

export default function ChassisMapPanel({ chassisIdentifier }: ChassisMapPanelProps) {
  const [chassis, setChassis] = useState<ChassisData | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorKind, setErrorKind] = useState<'not_found' | 'fetch' | null>(null);

  const { isLoaded, loadError } = useJsApiLoader(getGoogleMapsLoaderOptions());

  const fetchChassis = useCallback(async () => {
    const identifier = chassisIdentifier.trim();
    // No unit number on the order yet — nothing to look up. Treat as "no
    // device" rather than calling the API, since an empty identifier would
    // hit the list endpoint instead of a single-unit lookup.
    if (!identifier) {
      setChassis(null);
      setErrorKind('not_found');
      setLoading(false);
      return;
    }
    try {
      const response = await api.get(
        `/bb-radar/chassis/${encodeURIComponent(identifier)}`
      );
      setChassis(response.data);
      setErrorKind(null);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setChassis(null);
        setErrorKind('not_found');
      } else {
        console.error('Failed to fetch chassis data:', err);
        setErrorKind('fetch');
      }
    } finally {
      setLoading(false);
    }
  }, [chassisIdentifier]);

  useEffect(() => {
    setLoading(true);
    setErrorKind(null);
    fetchChassis();
    const interval = setInterval(fetchChassis, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchChassis]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (errorKind === 'not_found') {
    const identifier = chassisIdentifier.trim();
    return (
      <div className="p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md text-sm text-label">
        {identifier ? (
          <>
            No GPS device found for unit <span className="font-semibold">{identifier}</span>.
          </>
        ) : (
          'No unit number on this order yet.'
        )}
      </div>
    );
  }

  if (errorKind === 'fetch') {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md text-sm text-red-700 dark:text-red-400 flex items-center justify-between gap-3">
        <span>Failed to load chassis tracking data.</span>
        <button
          type="button"
          onClick={() => {
            setLoading(true);
            fetchChassis();
          }}
          className="px-3 py-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!chassis) return null;

  const hasPosition = chassis.latitude != null && chassis.longitude != null;
  const center = hasPosition
    ? { lat: chassis.latitude!, lng: chassis.longitude! }
    : fallbackCenter;

  return (
    <div className="space-y-3">
      <div className="rounded-md overflow-hidden border border-gray-200 dark:border-gray-700">
        {loadError ? (
          <div
            className="flex items-center justify-center bg-gray-50 dark:bg-gray-800"
            style={{ height: MAP_HEIGHT }}
          >
            <p className="text-sm text-red-500 dark:text-red-400">Failed to load Google Maps</p>
          </div>
        ) : !isLoaded ? (
          <div
            className="flex items-center justify-center bg-gray-50 dark:bg-gray-800"
            style={{ height: MAP_HEIGHT }}
          >
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : !hasPosition ? (
          <div
            className="flex items-center justify-center bg-gray-50 dark:bg-gray-800"
            style={{ height: MAP_HEIGHT }}
          >
            <p className="text-sm text-muted">Last position unknown</p>
          </div>
        ) : (
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={center}
            zoom={14}
            options={{
              disableDefaultUI: false,
              zoomControl: true,
              mapTypeControl: false,
              streetViewControl: false,
              fullscreenControl: true,
              clickableIcons: false,
            }}
          >
            <Marker position={center} title={chassis.identifier} />
          </GoogleMap>
        )}
      </div>

      <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
          <InfoRow
            label="Truck"
            value={matchChipText(chassis)}
            valueClass={
              chassis.match_confidence ? CONFIDENCE_CLASS[chassis.match_confidence] ?? '' : ''
            }
            tooltip={
              chassis.matched_since
                ? `Matched since ${new Date(chassis.matched_since).toLocaleString()}`
                : undefined
            }
          />
          <InfoRow
            label="Last Reported"
            value={chassis.last_reported_at ? timeAgo(chassis.last_reported_at) : '—'}
            tooltip={
              chassis.last_reported_at
                ? new Date(chassis.last_reported_at).toLocaleString()
                : undefined
            }
          />
          <InfoRow
            label="Status"
            value={
              chassis.is_moving === true
                ? `Moving${
                    chassis.velocity != null
                      ? ` (${Math.round(chassis.velocity * 2.237)} mph)`
                      : ''
                  }`
                : chassis.is_moving === false
                ? 'Stationary'
                : '—'
            }
          />
          <InfoRow
            label="Door"
            value={
              chassis.door_open === true
                ? 'Open'
                : chassis.door_open === false
                ? 'Closed'
                : '—'
            }
            valueClass={
              chassis.door_open === true ? 'text-amber-600 dark:text-amber-400 font-medium' : ''
            }
          />
          <InfoRow
            label="Battery"
            value={
              chassis.battery_state === false
                ? 'Low'
                : chassis.battery_state === true
                ? 'OK'
                : '—'
            }
            valueClass={
              chassis.battery_state === false
                ? 'text-red-600 dark:text-red-400 font-medium'
                : ''
            }
          />
          {chassis.temperature != null && (
            <InfoRow
              label="Temperature"
              value={`${chassis.temperature.toFixed(1)}°C / ${(
                chassis.temperature * 9 / 5 +
                32
              ).toFixed(1)}°F`}
            />
          )}
          {hasPosition && (
            <InfoRow
              label="Coordinates"
              value={`${chassis.latitude!.toFixed(4)}, ${chassis.longitude!.toFixed(4)}`}
              valueClass="text-[0.6875rem]"
            />
          )}
        </div>
      </div>
    </div>
  );
}
